import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { TripWithRelations } from "@ckgo2/shared/types";
import { Plus, Search, Plane, Edit, Trash2, Calendar, MapPin, Copy } from "lucide-react";
import { formatPrice } from "@ckgo2/shared/utils";

const DIFFICULTY_LABELS: Record<string, string> = {
  easy: "Snadná",
  moderate: "Střední",
  challenging: "Náročná",
  extreme: "Extrémní",
};

export default function TripsPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-trips"],
    queryFn: () => api.get<ApiResponse<TripWithRelations[]>>("/api/admin/trips"),
  });

  const duplicateMutation = useMutation({
    mutationFn: (tripId: string) =>
      api.post<ApiResponse<TripWithRelations>>(`/api/admin/trips/${tripId}/duplicate`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["admin-trips"] });
      if (response.data?.id) {
        navigate(`/trips/${response.data.id}`);
      }
    },
  });

  const trips = data?.data || [];

  // Filter trips
  const filteredTrips = trips.filter((trip) => {
    const matchesSearch =
      trip.title?.toLowerCase().includes(search.toLowerCase()) ||
      trip.destination?.name?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || trip.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání zájezdů. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Zájezdy
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Spravujte katalog zájezdů
          </p>
        </div>
        <Link to="/trips/new" className="btn-primary">
          <Plus className="h-5 w-5" />
          Nový zájezd
        </Link>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Hledat zájezdy..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full sm:w-48"
          >
            <option value="">Všechny stavy</option>
            <option value="draft">Koncept</option>
            <option value="published">Publikováno</option>
            <option value="archived">Archivováno</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
          </div>
        ) : filteredTrips.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <Plane className="mb-2 h-12 w-12 text-gray-300" />
            <p>Žádné zájezdy nenalezeny</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Zájezd
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Destinace
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Cena
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Délka
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500 dark:text-gray-400">
                  Akce
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredTrips.map((trip) => (
                <tr
                  key={trip.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {trip.featuredImage ? (
                        <img
                          src={trip.featuredImage}
                          alt={trip.title}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700">
                          <Plane className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {trip.title}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          /{trip.slug}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-300">
                        {trip.destination?.name || "-"}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatPrice(trip.priceFrom)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-300">
                        {trip.durationDays} dní
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {trip.status === "published" ? (
                      <span className="badge bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                        Publikováno
                      </span>
                    ) : trip.status === "draft" ? (
                      <span className="badge bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400">
                        Koncept
                      </span>
                    ) : (
                      <span className="badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Archivováno
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => duplicateMutation.mutate(trip.id)}
                        disabled={duplicateMutation.isPending}
                        className="btn-ghost btn-sm"
                        title="Duplikovat zájezd"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <Link
                        to={`/trips/${trip.id}`}
                        className="btn-ghost btn-sm"
                        title="Upravit"
                      >
                        <Edit className="h-4 w-4" />
                      </Link>
                      <button className="btn-ghost btn-sm text-error-600 hover:bg-error-50 hover:text-error-700 dark:text-error-400 dark:hover:bg-error-900/20" title="Smazat">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
